import React, { useState } from 'react';
import { VerificationModal } from '../common';

function getToolName(tool) {
  if (tool == null) return 'Unnamed tool';
  return tool?.name ?? tool?.title ?? 'Unnamed tool';
}

const RESERVED_KEYS = [
  'identificationId', 'toolId', 'tool', 'id', '_id',
  'createdAt', 'updatedAt', 'created_at', 'updated_at',
  'requiredSize', 'required_size',
];
const KEY_LABELS = {
  label: 'Label / Name',
  name: 'Name',
  requirement: 'Requirement',
  height: 'Height',
  width: 'Width',
  maxFileSize: 'Max file size',
  maxFiles: 'Max files',
  acceptedTypes: 'Accepted types',
  defaultConfig: 'Default config',
};

function keyToLabel(key) {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

/** Show height & width inputs if the master tool has requiredSize, or the entry has requiredSize (from template API), or entry already has height/width. */
function getEditableKeys(entry) {
  const rawKeys = Object.keys(entry).filter((k) => !RESERVED_KEYS.includes(k));
  const toolRequiredSize = entry.tool?.requiredSize ?? entry.tool?.required_size;
  const entryRequiredSize = entry.requiredSize ?? entry.required_size;
  const entryHasDimensions = entry.height != null || entry.width != null;
  const hasRequiredSize = (toolRequiredSize != null) || (entryRequiredSize != null) || entryHasDimensions;
  if (!hasRequiredSize) {
    return rawKeys.filter((k) => k !== 'height' && k !== 'width');
  }
  const keys = [...rawKeys];
  if (!keys.includes('height')) keys.push('height');
  if (!keys.includes('width')) keys.push('width');
  return keys;
}

/** Small-font field for tool entry (so user can tell template vs tool fields). */
function ConfigField({ idPrefix, keyName, value, onChange }) {
  const label = keyToLabel(keyName);
  const inputId = idPrefix ? `${idPrefix}-${keyName}` : keyName;
  const isNum = typeof value === 'number';
  const isBool = typeof value === 'boolean';
  const isObj = value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArr = Array.isArray(value);
  const fieldCls = 'tool-entry-field';

  if (isBool) {
    return (
      <div className={`form-group mb-2 ${fieldCls}`}>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id={inputId}
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <label className="form-check-label" htmlFor={inputId}>{label}</label>
        </div>
      </div>
    );
  }
  if (isNum || (keyName === 'height' || keyName === 'width')) {
    const numVal = typeof value === 'number' ? value : (value === '' || value == null ? '' : Number(value));
    return (
      <div className={`form-group mb-2 ${fieldCls}`}>
        <label className="form-label" htmlFor={inputId}>{label}</label>
        <input
          type="number"
          id={inputId}
          className="form-control form-control-sm"
          value={numVal === '' ? '' : numVal}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? '' : Number(v));
          }}
          placeholder={label}
        />
      </div>
    );
  }
  if (isArr) {
    const str = value.join(', ');
    return (
      <div className={`form-group mb-2 ${fieldCls}`}>
        <label className="form-label" htmlFor={inputId}>{label}</label>
        <input
          type="text"
          id={inputId}
          className="form-control form-control-sm"
          value={str}
          onChange={(e) => onChange(e.target.value ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean) : [])}
          placeholder="Comma separated"
        />
      </div>
    );
  }
  if (isObj) {
    const str = JSON.stringify(value);
    return (
      <div className={`form-group mb-2 ${fieldCls}`}>
        <label className="form-label" htmlFor={inputId}>{label}</label>
        <input
          type="text"
          id={inputId}
          className="form-control form-control-sm font-monospace"
          value={str}
          onChange={(e) => {
            try {
              const v = e.target.value.trim();
              onChange(v ? JSON.parse(v) : {});
            } catch {
              onChange(e.target.value);
            }
          }}
          placeholder="{}"
        />
      </div>
    );
  }
  return (
    <div className={`form-group mb-2 ${fieldCls}`}>
      <label className="form-label" htmlFor={inputId}>{label}</label>
      <input
        type="text"
        id={inputId}
        className="form-control form-control-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={label}
      />
    </div>
  );
}

/**
 * Left side: selected tools as outline cards with small-font fields.
 */
export default function SelectedToolsCards({
  selectedEntries = [],
  onRemoveEntry,
  onMoveUp,
  onMoveDown,
  onUpdateEntry,
}) {
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);

  const handleRemoveClick = (index) => {
    setPendingRemoveIndex(index);
  };

  const handleConfirmRemove = () => {
    if (pendingRemoveIndex != null) {
      onRemoveEntry?.(pendingRemoveIndex);
      setPendingRemoveIndex(null);
    }
  };

  const pendingEntry = pendingRemoveIndex != null ? selectedEntries[pendingRemoveIndex] : null;
  const pendingToolName = pendingEntry ? getToolName(pendingEntry.tool) : '';

  if (selectedEntries.length === 0) {
    return (
      <div className="selected-tools-cards">
        <h3 className="selected-tools-cards__title h6 mb-2">Selected tools</h3>
        <p className="text-muted small mb-0">No tools added yet. Add from the list on the right.</p>
      </div>
    );
  }

  return (
    <div className="selected-tools-cards">
      <h3 className="selected-tools-cards__title h6 mb-2">Selected tools</h3>
      <p className="selected-tools-cards__hint text-muted small mb-3">
        Edit properties for this template. Reorder with arrows.
      </p>
      <div className="selected-tools-cards__list">
        {selectedEntries.map((entry, index) => {
          const editableKeys = getEditableKeys(entry);
          return (
            <div
              key={`entry-${index}-${entry.identificationId ?? entry.toolId ?? index}`}
              className="selected-tools-cards__card card"
            >
              <div className="card-body">
                <div className="selected-tools-cards__card-header">
                  <span className="selected-tools-cards__card-order">{index + 1}</span>
                  <span className="selected-tools-cards__card-name">{getToolName(entry.tool)}</span>
                  <div className="selected-tools-cards__card-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onMoveUp?.(index)}
                      disabled={index === 0}
                      title="Move up"
                      aria-label="Move up"
                    >
                      <i className="bi bi-chevron-up" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => onMoveDown?.(index)}
                      disabled={index === selectedEntries.length - 1}
                      title="Move down"
                      aria-label="Move down"
                    >
                      <i className="bi bi-chevron-down" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveClick(index)}
                      title="Remove"
                      aria-label="Remove"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>
                <div className="selected-tools-cards__card-fields">
                  {editableKeys.length === 0 ? (
                    <p className="text-muted small mb-0">No editable properties.</p>
                  ) : (
                    editableKeys.map((key) => (
                      <ConfigField
                        key={key}
                        idPrefix={`entry-${index}`}
                        keyName={key}
                        value={entry[key]}
                        onChange={(newVal) => onUpdateEntry?.(index, { ...entry, [key]: newVal })}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <VerificationModal
        show={pendingRemoveIndex != null}
        onClose={() => setPendingRemoveIndex(null)}
        onConfirm={handleConfirmRemove}
        title="Remove tool"
        message={pendingToolName ? `Remove "${pendingToolName}" from this template?` : 'Remove this tool from the template?'}
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}
